import { PortfolioChart } from '@/components/portal-modules/PortalChart';
import { useAppSelector } from '@/state/hooks';
import { selectCurrencyConfigs } from '@/state/reducers/ohoSlice';
import { BalancesSummaryReturn } from '@/types/sso';
import useLanguage from '@/utils/hooks/useLanguage';
import { useSso } from '@/utils/providers/SSOProvider';
import { useMemo } from 'react';
// Professional financial palette
const COLORS = [
    '#0ea5e9',
    '#6366f1',
    '#10b981',
    '#f59e0b',
    '#ec4899',
    '#8b5cf6',
    '#14b8a6',
    '#f97316',
    '#64748b',
    '#d946ef'
];

export interface Position {
    id: string;
    instrument: string;
    assetClass: 'Equity' | 'FX' | 'Fixed Income' | 'Commodity' | 'Crypto';
    instrumentCcy: string;
    marketValue: number; // in base currency (USD)
}

interface PortalBalancesProps {
    balances?: BalancesSummaryReturn;
    netEquity?: {
        netEquity: number;
    };
}

function PortalBalances({ balances, netEquity }: PortalBalancesProps) {
    const { balances: balancesData, baseCurrency } = balances || {};

    const currencyConfigs = useAppSelector(selectCurrencyConfigs);
    const { accountType, isSpecificAccountSelected } = useSso();

    const lang = useLanguage();

    const filteredBalances = useMemo(() => {
        if (balancesData)
            if (isSpecificAccountSelected) {
                const matchingAccount = balancesData.accountBased.find(
                    (balance) => balance.accountType === accountType.value
                );

                if (matchingAccount) {
                    return matchingAccount.subRows.map((currency) => {
                        return {
                            amountInBaseCurrency: currency.totalCurrencyAmountInBaseCurrency,
                            currencyName: currency.currencyName
                        };
                    });
                }
            } else {
                return balancesData.currencyBased.map((currency) => {
                    return {
                        amountInBaseCurrency: currency.totalCurrencyAmountInBaseCurrency,
                        currencyName: currency.currencyName
                    };
                });
            }
        return [];
    }, [balancesData, accountType, isSpecificAccountSelected]);

    const totalBalancesValue = useMemo(() => {
        if (filteredBalances) {
            return filteredBalances.reduce((sum, balance) => {
                const itemValue = balance.amountInBaseCurrency || 0;
                return sum + itemValue;
            }, 0);
        }
        return 0;
    }, [filteredBalances]);

    const assetClassData = useMemo(() => {
        if (filteredBalances) {
            const map = new Map<string, number>();
            filteredBalances.forEach((balance) => {
                const config = currencyConfigs.find(
                    (cfg) => cfg.show === balance.currencyName || cfg.code === balance.currencyName
                );
                const keyLabel = config?.type || 'Others';

                const currentValue = map.get(keyLabel) || 0;
                const itemValue = balance.amountInBaseCurrency || 0;
                map.set(keyLabel, currentValue + itemValue);
            });

            const entries = Array.from(map.entries());

            const absTotal = entries.reduce((sum, [name, value]) => {
                const itemValue = Math.abs(value);
                return sum + itemValue;
            }, 0);

            const result = entries.map(([name, value], index) => ({
                name,
                value,
                absValue: Math.abs(value),
                percentage: Math.abs((value / absTotal) * 100),
                color: COLORS[index % COLORS.length]
            }));
            // .filter((item) => item.value > 0);

            return result.sort((a, b) => b.value - a.value);
        }
    }, [currencyConfigs, filteredBalances]);

    const assetDistributionData = useMemo(() => {
        if (filteredBalances) {
            const map = new Map<string, number>();
            filteredBalances.forEach((balance) => {
                const keyLabel = balance.currencyName;

                const currentValue = map.get(keyLabel) || 0;
                const itemValue = balance.amountInBaseCurrency || 0;
                map.set(keyLabel, currentValue + itemValue);
            });

            const entries = Array.from(map.entries());

            const absTotal = entries.reduce((sum, [name, value]) => {
                const itemValue = Math.abs(value);
                return sum + itemValue;
            }, 0);

            const result = entries.map(([name, value], index) => ({
                name,
                value,
                absValue: Math.abs(value),
                percentage: Math.abs((value / absTotal) * 100),
                color: COLORS[index % COLORS.length]
            }));
            // .filter((item) => item.value > 0);

            return result.sort((a, b) => b.value - a.value);
        }
    }, [currencyConfigs, filteredBalances]);

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {/* Asset Class Distribution */}
            {balances && assetClassData && (
                <div className="h-full">
                    <PortfolioChart
                        title={lang.portal.assetClassAllocation}
                        data={assetClassData}
                        totalValue={totalBalancesValue}
                        baseCurrency={baseCurrency || ''}
                    />
                </div>
            )}
            {balances && assetDistributionData && (
                <div className="h-full">
                    <PortfolioChart
                        title={lang.portal.currencyExposure}
                        data={assetDistributionData}
                        totalValue={totalBalancesValue}
                        baseCurrency={baseCurrency || ''}
                    />
                </div>
            )}
        </div>
    );
}

export default PortalBalances;
